import { useMemo } from 'react';
import { Community, CommunityUser } from 'services/graphqltypes';
import { gql, useQuery } from '@apollo/client';

const CURRENT_USER_COMMUNITIES_PERMISSIONS = gql`
query currentUserCommunitiesPermissions {
  currentUser {
    isAdmin
    communityUsers {
      nodes {
        id
        communityRolePermissions {
          communityId
          createEventCourse
        }
        community {
          id
          name
          avatarUrl
          aboutOneliner
          isShopEnabled
          email
          phone
          website
          parentCommunity {
            id
            name
          }
        }
      }
    }
  }
}
`;

const ALL_ACTIVE_COMMUNITIES = gql`
query allActiveCommunities {
  communities(filter: {isDisabled: {notEqualTo: true}}) {
    nodes {
      id
      name
      avatarUrl
      aboutOneliner
      isShopEnabled
      email
      phone
      website
      parentCommunity {
        id
        name
      }
    }
  }
}
`;

export const useLoadCommunitiesWithCreateActivityPermissionResult = () => {
  const {
    loading,
    error,
    data,
    refetch: refetchPermissions,
  } = useQuery(CURRENT_USER_COMMUNITIES_PERMISSIONS);
  const {
    loading: adminCommunitiesLoading,
    error: adminCommunitiesError,
    data: adminCommunitiesData,
    refetch: refetchAdminCommunities,
  } = useQuery(ALL_ACTIVE_COMMUNITIES, {
    skip: !data?.currentUser?.isAdmin,
  });

  const communities = useMemo(() => {
    if (loading || error || !data?.currentUser) {
      return [] as Community[];
    }
    if (data.currentUser.isAdmin) {
      return adminCommunitiesData?.communities?.nodes || [];
    }
    return data.currentUser.communityUsers?.nodes
      .filter((communityUser: CommunityUser) =>
        communityUser.communityRolePermissions?.createEventCourse)
      .map((communityUser: CommunityUser) => communityUser.community) || [];
  }, [loading, error, data, adminCommunitiesData]);

  return {
    communities,
    loading: loading || adminCommunitiesLoading,
    error: error || adminCommunitiesError,
    refetch: async () => {
      const refreshedPermissions = await refetchPermissions();
      if (refreshedPermissions.data?.currentUser?.isAdmin) {
        await refetchAdminCommunities();
      }
    },
  };
};

// this hook returns an array of community objects (Community[])
// of communities where current user has createEventCourse permission
const useLoadCommunitiesWithCreateActivityPermission = (): Community[] => {
  return useLoadCommunitiesWithCreateActivityPermissionResult().communities;
};

export default useLoadCommunitiesWithCreateActivityPermission;
